import { FC, useState } from 'react'

import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

import { useSpotifyCampaignDetails } from 'src/Redux/campaignDetailsApi/useSpotifyCampaignDetails'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { getValueOrZero } from 'src/Constants/functions'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import {
  requestCampaignAnalysisClicked,
  reportConcernClicked,
  reachNewCuratorsClicked,
  expandReachTiktokClicked,
  boostReachTiktokClicked,
} from 'src/Helpers/TagManager/SpotifyCTAEvents'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import {
  ArtistReviewFeedbackBody,
  usePostArtistReviewFeedbackMutation,
} from 'src/Redux/artistSpotifyCampaignReviewApi'
import { useAddFavoriteCuratorMutation } from 'src/Redux/favoriteCuratorsApi'
import { useTotalPaidCampaignsCountQuery } from 'src/Redux/metadataApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useCampaignDetailsReviews } from 'src/Hooks/useCampaignDetailsReviews'
import { useCampaignPayment } from 'src/Hooks/useCampaignPayment'

import { SpotifyDetailsBreadcrumbs } from './Components/SpotifyDetailsBreadcrumbs'
import { ResultSection } from './Components/ResultSection'
import { SummarySection } from './Components/SummarySection'
import { useSpotifyCampaignDetailsPage } from './useSpotifyCampaignDetailsPage'
import { Survey } from './Components/Survey'
import { MessageBlock } from './Components/MessageBlock'
import { FavoriteCuratorModal } from './Components/FavoriteCuratorModal'
import { IntercomMessageEnum } from './Components/CampaignUpsell/CampaignUpsellCard/helpers'
import { SpotifyTrackLinkBlock } from './Components/SpotifyTrackLinkBlock'
import { getIsTrackLinkBlockVisible } from './helpers'

export { getIsTrackLinkBlockVisible }

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 40px;
    padding-bottom: 56px;
  }
`

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    flex-direction: column;
  }
`

export enum UpsellType {
  BOOST = 'boost',
  EXPAND = 'expand',
}
export interface OnFavoriteCuratorParams {
  curatorUserId: number
  isFavorite: boolean
}

export const SpotifyCampaignDetailsPage: FC = () => {
  const navigate = useNavigate()
  const { showNewMessage, update } = useIntercom()
  const { onRelaunchCampaign, onUpsellCampaign } =
    useSpotifyCampaignDetailsPage()
  const { campaignDetails, isLoading } = useSpotifyCampaignDetails()
  const payment = useCampaignPayment(
    CampaignPlatformTypeEnum.SPOTIFY,
    campaignDetails?.id,
  )
  const { reviews } = useCampaignDetailsReviews()
  const { matchingType } = useSpotifyCampaignStatus()
  const isManualMatching = matchingType === SpotifyCampaignMatchingType.MANUAL
  const { data: paidCampaignsCountData } = useTotalPaidCampaignsCountQuery()
  const userId = useSelector(getUserId)
  const genresList = campaignDetails?.Track?.genres.map(
    (genre) => genre.genreName || '',
  )

  const [isFavoriteCuratorModalOpen, setIsFavoriteCuratorModalOpen] =
    useState(false)

  const [favoriteCurator, { isLoading: onAddFavoriteCuratorLoading }] =
    useAddFavoriteCuratorMutation()
  const [postFeedback] = usePostArtistReviewFeedbackMutation()

  const campaignData = {
    userId: String(userId),
    campaignId: campaignDetails?.id,
    campaignNumber: getValueOrZero(paidCampaignsCountData?.totalCount),
    campaignType: campaignDetails?.type,
  }

  const isStatusForRelaunch =
    campaignDetails?.status &&
    [
      CampaignStatusEnum.STOPPED,
      CampaignStatusEnum.ENDED,
      CampaignStatusEnum.STOPPED_BY_ADMIN,
    ].includes(campaignDetails?.status)

  const isNoActiveCampaignsWithSameTrack =
    campaignDetails?.trackActiveCampaignsCount === 0

  const isTrackLinkBlockVisible = getIsTrackLinkBlockVisible(
    campaignDetails?.isPreRelease,
    campaignDetails?.releaseDate,
    campaignDetails?.status,
    campaignDetails?.playlists_adds,
    campaignDetails?.Track?.spotifyId,
  )

  const isRelaunchAvailable =
    isNoActiveCampaignsWithSameTrack &&
    isStatusForRelaunch &&
    matchingType !== 'manual' &&
    !campaignDetails?.isPreRelease

  const onRelaunchCampaignClick = (isCTA?: boolean): void => {
    if (isCTA) {
      reachNewCuratorsClicked(campaignData)
    }
    campaignDetails?.id && onRelaunchCampaign(campaignDetails?.id)
  }

  const onUpsellMetaAds = (): void => {
    navigate(ArtistRoutesEnum.CreateAds)
  }

  const onUpsellCampaignClick = (upsellType?: UpsellType): void => {
    if (upsellType === UpsellType.EXPAND) {
      expandReachTiktokClicked(campaignData)
    }
    if (upsellType === UpsellType.BOOST) {
      boostReachTiktokClicked(campaignData)
    }
    campaignDetails?.Track?.trackName &&
      onUpsellCampaign(campaignDetails?.Track?.trackName)
  }

  const onStartCampaignClick = (): void => {
    navigate(`${ArtistRoutesEnum.SpotifySchedule}/${campaignDetails?.id}`)
  }

  const onOpenIntercom = (message: IntercomMessageEnum): void => {
    if (message === IntercomMessageEnum.REQUEST_ANALYSIS) {
      requestCampaignAnalysisClicked(campaignData)
    }
    if (message === IntercomMessageEnum.REPORT_CONCERN) {
      reportConcernClicked(campaignData)
    }
    update({
      // now not work on intercom side
      customAttributes: {
        campaignId: String(campaignDetails?.id),
      },
      // work on intercom side
      companies: [
        {
          companyId: String(campaignDetails?.id),
        },
      ],
    })
    showNewMessage(message)
  }

  const onSubmitLikeDislike = (
    feedbackData: ArtistReviewFeedbackBody,
  ): void => {
    postFeedback(feedbackData)
  }

  const onFavoriteCurator = async ({
    curatorUserId,
    isFavorite,
  }: OnFavoriteCuratorParams): Promise<void> => {
    if (isFavorite) {
      return
    }
    await favoriteCurator(curatorUserId)
      .unwrap()
      .then(() => {
        setIsFavoriteCuratorModalOpen(true)
      })
  }

  return (
    <PageContainer>
      <Survey />
      <SpotifyDetailsBreadcrumbs />
      <MessageBlock isTrackLinkBlockVisible={isTrackLinkBlockVisible} />
      {isTrackLinkBlockVisible && campaignDetails?.id && (
        <SpotifyTrackLinkBlock campaignId={campaignDetails.id} />
      )}
      <Content>
        <SummarySection
          campaignStatus={campaignDetails?.status}
          payment={payment}
          loading={isLoading}
          spent={String(campaignDetails?.spent)}
          budget={String(campaignDetails?.budget)}
          trackCoverUrl={campaignDetails?.Track?.trackImage}
          trackName={campaignDetails?.Track?.trackName}
          trackAuthorName={campaignDetails?.Track?.authorName}
          startDate={campaignDetails?.start_date}
          endDate={campaignDetails?.end_date}
          genres={genresList}
          onRelaunchCampaign={onRelaunchCampaignClick}
          onUpsellCampaign={onUpsellCampaignClick}
          refund={String(campaignDetails?.refund)}
          isRelaunchAvailable={isRelaunchAvailable}
          matchingType={matchingType}
          isPreRelease={campaignDetails?.isPreRelease}
        />

        <ResultSection
          startDate={campaignDetails?.start_date}
          campaignStatus={campaignDetails?.status}
          payment={payment}
          loading={isLoading}
          reviewsCount={campaignDetails?.reviewed_count}
          placementsCount={campaignDetails?.playlists_adds}
          reviews={reviews}
          userId={Number(userId)}
          campaignId={campaignDetails?.id}
          trackName={campaignDetails?.Track?.trackName}
          trackAuthorName={campaignDetails?.Track?.authorName}
          trackCoverUrl={campaignDetails?.Track?.trackImage}
          onStartCampaignClick={onStartCampaignClick}
          onOpenIntercom={onOpenIntercom}
          onRelaunch={onRelaunchCampaignClick}
          onUpsellMetaAds={onUpsellMetaAds}
          isRelaunchAvailable={isRelaunchAvailable}
          onSubmitLikeDislike={onSubmitLikeDislike}
          onFavoriteCurator={onFavoriteCurator}
          onAddFavoriteCuratorLoading={onAddFavoriteCuratorLoading}
          isManualMatching={isManualMatching}
        />
      </Content>

      <FavoriteCuratorModal
        isOpen={isFavoriteCuratorModalOpen}
        onClose={() => setIsFavoriteCuratorModalOpen(false)}
      />
    </PageContainer>
  )
}
